<template>
  <va-form
    :id="abTest ? abTest.id : ''"
    ref="form"
    :item="abTest"
    v-model="form"
    :disable-redirect="disableRedirect"
    :required-fields-set="requiredFieldsSet"
    :after-save="handleAfterSave"
    @finishedSaving="finishedSaving">
    <Modal
      width="452"
      :value="showModalAfterSave"
      :title="$t('resources.ab_tests.script_modal_title')"
      :subtitle="$t('resources.ab_tests.script_modal_subtitle')"
      :persistent="true"
      :dialog-icon="'danger'"
      @cancel="handleCloseModalButton">
      <template #default>
        <div class="script-modal-wrapper">
          <div class="script-modal-label">
            {{ $t("resources.ab_tests.script_modal_label") }}
          </div>

          <div>
            <CopyText display-icon :value="`<script src='${form.script}'></script>`"></CopyText>
          </div>
        </div>

        <div class="script-modal-content">&lt;script src="{{ form.script }}"&gt;&lt;/script&gt;</div>
      </template>
      <template #actions>
        <div class="script-modal-action-wrapper">
          <CopyText
            class="script-modal-action-button"
            :value="`<script src='${form.script}'></script>`"
            :text-disclaimer="$t('resources.ab_tests.script_modal_button_text')"></CopyText>
        </div>
      </template>
    </Modal>

    <v-card class="spacing-bottom--24">
      <v-card-title>
        <div class="details-title-wrapper">
          <span>{{ $t("resources.ab_tests.details_title") }}</span>
          <div>
            <v-chip v-if="form.status === AB_TEST_STATUSES.ACTIVE" class="status-chip active-chip" color="info-120">
              {{ $t("resources.ab_tests.active") }}
            </v-chip>
            <v-chip class="status-chip" v-if="form.status === AB_TEST_STATUSES.DRAFT">
              {{ $t("resources.ab_tests.draft") }}
            </v-chip>
            <v-chip class="status-chip inactive-chip" v-if="form.status === AB_TEST_STATUSES.INACTIVE">
              {{ $t("resources.ab_tests.inactive") }}
            </v-chip>
          </div>
        </div>
      </v-card-title>
      <va-text-input
        class="spacing-bottom--16 spacing-top--16"
        v-model="form.name"
        :label="$t('resources.ab_tests.fields.name')"
        :placeholder="$t('resources.ab_tests.placeholders.name')"
        required>
      </va-text-input>
      <va-autocomplete-input
        class="spacing-bottom--16"
        v-model="form.owner"
        :placeholder="$t('resources.ab_tests.fields.owner')"
        source="owner"
        :label="$t('resources.ab_tests.fields.owner')"
        reference="users"
        search-query="email"
        item-text="email"
        item-value="@id"
        hide-details="auto"
        required
        :min-chars="0"
        :items-per-page="5"
        custom-item
        showLoadMore>
        <template #item="{ item, on }">
          <!-- Profile -->
          <v-list-item v-on="on" class="my-2">
            <v-list-item-content>
              <div class="d-flex flex-col">
                <v-list-item-title>
                  <div>{{ item.firstName }} {{ item.lastName }}</div>
                  <div class="owner-subtitle">{{ item.email }}</div>
                </v-list-item-title>
              </div>
            </v-list-item-content>
          </v-list-item>
        </template>
      </va-autocomplete-input>

      <div class="server-side-block" v-if="form.type === 'page'">
        <div class="server-side-block__text">
          {{ $t("resources.ab_tests.server_side") }}
        </div>
        <v-switch
          v-model="form.serverSide"
          class="ml-3 split-block__switch"
          inset
          color="primary"
          :hide-details="'auto'"
          :disabled="isActive"
          :ripple="false"
          @change="serverSideHandler" />
      </div>
      <v-select
        v-if="$can('view', 'A/B redirect type') && !form.serverSide"
        v-model="form.redirectType"
        :items="AB_REDIRECT_METHODS"
        required
        hide-details="auto"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
        <template v-slot:label>
          {{ $t("resources.ab_tests.fields.redirect_method") }} <span class="required__icon">*</span>
          <info-tooltip
            v-if="form.redirectType === AB_REDIRECT_TYPES.REPLACE"
            :info="$t('resources.ab_tests.tooltips.non_history')"></info-tooltip>
          <info-tooltip
            v-if="form.redirectType === AB_REDIRECT_TYPES.HREF"
            :info="$t('resources.ab_tests.tooltips.history')"></info-tooltip>
        </template>
      </v-select>
      <va-text-input
        class="spacing-bottom--16 spacing-top--16"
        v-model="form.notes"
        :label="$t('resources.ab_tests.fields.notes')"
        :placeholder="$t('resources.ab_tests.placeholders.notes')">
      </va-text-input>
      <div v-if="showScriptField">
        <va-text-input
          class="spacing-bottom--8 spacing-top--16"
          v-model="form.script"
          disabled
          :label="$t('resources.ab_tests.fields.script')"
          :placeholder="$t('resources.ab_tests.placeholders.script')">
          <template #append>
            <CopyText class="mr-3" display-icon :value="form.script"></CopyText>
          </template>
        </va-text-input>
        <div class="script-description">
          {{ $t("resources.ab_tests.script_description") }}
        </div>
      </div>
    </v-card>
    <!-- Goals -->
    <Modal
      width="400"
      :value="displayDeleteGoalModal"
      :title="$t('resources.labels.goal_delete_modal_title')"
      :subtitle="$t('resources.labels.goal_delete_modal_subtitle')"
      :cta-text="$t('generic.buttons.delete')"
      :cta-color="'error'"
      :dialog-icon="'danger'"
      @confirm="confirmDeleteGoal"
      @cancel="cancelDeleteGoal">
    </Modal>

    <Modal
      width="400"
      :value="isGoalCreateActive"
      hideCloseIcon
      hideActions
      contentClass="goal-create-modal"
      @cancel="isGoalCreateActive = false">
      <template #title>
        <span class="create-label-title">
          {{ $t("resources.domains.domain_label") }}
        </span>
      </template>
      <template>
        <v-form ref="goalForm" @submit.prevent="saveGoal" class="goal-form">
          <v-text-field
            class="goal-name-input"
            :label="$t('resources.ab_tests.goal_name')"
            :placeholder="$t('resources.ab_tests.goal_name_placeholder')"
            persistent-placeholder
            v-model="newGoal.name"
            :rules="[v => !!v || $t('resources.ab_tests.goal_name_required')]" />
          <va-color-picker :title="$t('resources.ab_tests.goal_color')" v-model="newGoal.color" />
          <div class="goal-create-button-wrapper">
            <v-btn @click="isGoalCreateActive = false" class="goal-cancel-button" outlined>
              {{ $t("resources.ab_tests.cancel") }}
            </v-btn>
            <v-btn type="submit" color="primary">{{ $t("resources.ab_tests.create") }}</v-btn>
          </div>
        </v-form>
      </template>
    </Modal>
    <v-card class="spacing-bottom--24">
      <v-card-title class="card-title goal-card-title-wrapper">
        <div>
          {{ $t("resources.ab_tests.goals") }}
        </div>
        <div class="goal-add-button" @click="isGoalCreateActive = true">
          <plus-icon class="fill--primary mr-1"></plus-icon>
          {{ $t("resources.ab_tests.create_goal") }}
        </div>
      </v-card-title>
      <v-card-subtitle
        v-if="goals.length === 0 && !isGoalCreateActive && !goalListLoading"
        class="goals-empty-subtitle">
        {{ $t("resources.ab_tests.goals_empty") }}
      </v-card-subtitle>

      <div v-if="goals.length > 0 || goalListLoading" class="goal-select-wrapper-block">
        <div class="goal-select-wrapper-block-item">
          <v-autocomplete
            class="goal-primary-select"
            :label="$t('resources.ab_tests.goal_primary')"
            :placeholder="$t('resources.ab_tests.goal_primary_placeholder')"
            persistent-placeholder
            item-text="name"
            item-value="@id"
            append-icon="mdi-chevron-down"
            :items="goals"
            clearable
            v-model="form.goal">
            <template #item="{ item }">
              <v-row class="goal-select-item">
                <v-avatar class="goal-badge ml-4 mr-2" size="24" :color="item.color"></v-avatar>
                <span class="goal-title">{{ item.name }}</span>
                <v-icon
                  v-show="false"
                  class="goal-select-item__close-icon"
                  :key="item.name"
                  @click.prevent.stop="deleteGoal(item)"
                  size="16"
                  color="icon">
                  mdi-trash-can-outline
                </v-icon>
              </v-row>
            </template>
            <template #selection="{ item }">
              <v-avatar class="goal-badge mr-2" size="24" :color="item.color"></v-avatar> {{ item.name }}
            </template>
            <template #no-data>
              <div class="v-list-item theme--light">
                <div class="v-list-item__content">
                  <div class="v-list-item__title">
                    {{ $t("resources.ab_tests.no_goals_found") }}
                  </div>
                </div>
              </div>
            </template>
          </v-autocomplete>
        </div>

        <div class="goal-select-wrapper-block-item">
          <v-autocomplete
            :class="[
              'additional-goal-autocomplete',
              additionalGoalSearch && additionalGoalSearch.length && 'v-autocomplete--search-added',
            ]"
            ref="additionalGoalAutoComplete"
            :label="$t('resources.ab_tests.goal_additional')"
            :placeholder="$t('resources.ab_tests.goal_additional_placeholder')"
            return-object
            item-text="name"
            :items="goals"
            :search-input.sync="additionalGoalSearch"
            append-icon="mdi-chevron-down"
            prepend-inner-icon="mdi-plus"
            multiple
            persistent-placeholder
            hide-details
            v-model="selectedAdditionalGoals">
            <template #no-data>
              <div class="v-list-item theme--light">
                <div class="v-list-item__content">
                  <div class="v-list-item__title">
                    {{ $t("resources.ab_tests.no_goals_found") }}
                  </div>
                </div>
              </div>
            </template>
            <template #selection> </template>
            <template #item="{ item }">
              <div
                @mouseover="hoveredAdditionalGoal = item"
                @mouseleave="hoveredAdditionalGoal = null"
                class="additional-goal-item-wrapper">
                <div class="additional-goal-item-inner-wrapper">
                  <div class="v-list-item__action">
                    <v-icon>{{
                      $refs["additionalGoalAutoComplete"].hasItem(item)
                        ? "mdi-checkbox-marked"
                        : "mdi-checkbox-blank-outline"
                    }}</v-icon>
                  </div>
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      <v-avatar class="goal-badge mr-2" size="24" :color="item.color"></v-avatar>
                      {{ item.name }}
                    </div>
                  </div>
                </div>
                <v-icon
                  v-show="hoveredAdditionalGoal === item"
                  class="goal-select-item__close-icon"
                  :key="item.name"
                  @click.prevent.stop="deleteGoal(item)"
                  size="16"
                  color="icon">
                  mdi-trash-can-outline
                </v-icon>
              </div>
            </template>
          </v-autocomplete>
          <div
            :class="[
              'd-flex',
              'flex-wrap',
              'selection-chip__wrapper',
              selectedAdditionalGoals.length ? 'selection-chip__wrapper--has-selection' : '',
            ]">
            <template v-for="item in selectedAdditionalGoals">
              <v-chip
                v-if="item.id"
                class="additional-goal-chip"
                :key="`${item.id}-key`"
                :color="getGoalChipColor(item)"
                :style="{ color: item.color }">
                <span class="pr-2">
                  {{ item.name }}
                </span>
                <v-icon small @click="$refs['additionalGoalAutoComplete'].selectItem(item)">mdi-close</v-icon>
              </v-chip>
            </template>
          </div>
        </div>
      </div>
    </v-card>
    <v-card class="spacing-bottom--24">
      <v-card-title>
        <div class="variants-title-wrapper">
          <span>
            {{ $t("resources.ab_tests.variants_title") }}
          </span>
          <div v-if="$can('view', 'A/B redirect type')" class="split-block">
            <div class="split-block__column">
              <div class="split-block__row">
                <div class="split-block__text">
                  {{ $t("resources.ab_tests.split_evenly") }}
                </div>
                <info-tooltip
                  icon="mdi-information-outline"
                  :info="$t('resources.ab_tests.tooltips.traffic')"></info-tooltip>
              </div>
              <div v-if="splitEvenly" class="split-block__row">
                <v-checkbox
                  hide-details="auto"
                  class="split-block__checkbox"
                  color="primary"
                  v-model="statePooling"
                  @change="statePoolingHandler" />
                <div class="split-block__subtext">
                  {{ $t("resources.ab_tests.use_state_pooling") }}
                </div>
                <info-tooltip
                  icon="mdi-information-outline"
                  :info="$t('resources.ab_tests.tooltips.split_evenly_description')"></info-tooltip>
              </div>
            </div>

            <v-switch
              v-model="splitEvenly"
              class="ml-3 split-block__switch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false"
              :disabled="!isDraft"
              @change="splitEvenlyHandler"></v-switch>
          </div>
        </div>
      </v-card-title>
      <div class="domain-input-wrapper">
        <div class="domain-input-wrapper__label">
          {{ $t("resources.ab_tests.original") }}
        </div>

        <v-row>
          <v-col :cols="3">
            <v-select
              v-model="form.type"
              :items="ORIGINAL_SOURCES"
              required
              item-value="value"
              item-text="text"
              hide-details
              :disabled="!isDraft && isEdit"
              append-icon="mdi-chevron-down"
              @change="handleSourceTypeChange"
              :menu-props="{
                offsetOverflow: true,
                offsetY: true,
              }">
              <template #label>
                <div class="input-label">
                  {{ $t("resources.ab_tests.fields.source") }}<span class="required__icon">*</span>
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col :cols="8" v-if="form.type === 'page'">
            <div class="domain-slug-wrapper">
              <v-autocomplete
                class="page-select"
                v-model="form.domain"
                required
                :disabled="!isDraft && isEdit"
                :placeholder="$t('resources.ab_tests.placeholders.domain')"
                :rules="[v => !!v || $t('va.forms.required_field', { field: 'Domain' })]"
                :items="domainsList"
                append-icon="mdi-chevron-down"
                hide-details="auto"
                persistent-placeholder>
                <template #label>
                  <div class="input-label">
                    {{ $t("resources.ab_tests.fields.domain") }}<span class="required__icon">*</span>
                  </div>
                </template>
              </v-autocomplete>
              <va-autocomplete-input
                class="page-autocomplete"
                v-model="form.page"
                :disabled="(!isDraft && isEdit) || !form.domain"
                :placeholder="$t('resources.ab_tests.placeholders.url')"
                source="page"
                :label="$t('resources.ab_tests.fields.slug')"
                reference="pages"
                search-query="slug"
                item-text="slug"
                item-value="@id"
                hide-details="auto"
                :filter="domainFilter"
                required
                :min-chars="0"
                :items-per-page="5"
                showLoadMore>
                <template v-slot:required-symbol>
                  <slot>
                    <span>*</span>
                  </slot>
                </template>
              </va-autocomplete-input>
            </div>

            <div class="source-full-url">
              <a :href="sourceFullUrl" target="_blank">
                {{ sourceFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="8" v-else-if="form.type === 'form'">
            <div class="domain-slug-wrapper">
              <va-autocomplete-input
                class="form-autocomplete"
                v-model.trim="formDomain"
                :disabled="!isDraft && isEdit"
                :placeholder="$t('resources.ab_tests.placeholders.form')"
                :label="$t('resources.ab_tests.fields.form')"
                reference="external_forms"
                item-text="url"
                item-value="url"
                hide-details="auto"
                required
                :min-chars="0"
                :items-per-page="5"
                @input="concatFormUrl"
                @blur="checkFormSourceUrl"
                key="source-form-domain"
                :filter="formDomainFilter"
                showLoadMore>
                <template v-slot:required-symbol>
                  <slot>
                    <span>*</span>
                  </slot>
                </template>
              </va-autocomplete-input>
              <va-text-input
                v-model.trim="formDomainParams"
                :disabled="!isDraft && isEdit"
                class="spacing-bottom--4"
                :label="$t('resources.ab_tests.fields.query_parameters')"
                :prefix="'/?'"
                :rules="[rules.queryParamsValidation]"
                @input="concatFormUrl">
              </va-text-input>
            </div>
            <div class="source-url__disclaimer" v-if="!validUrl">
              <v-icon size="medium" color="info">mdi-information</v-icon>
              {{ $t("resources.ab_tests.first_party_cookie") }}
            </div>
            <div class="source-full-url">
              <a :href="sourceFullUrl" target="_blank">
                {{ sourceFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="8" v-else>
            <va-text-input
              v-model="form.link"
              :disabled="!isDraft && isEdit"
              class="spacing-bottom--4"
              :label="$t('resources.ab_tests.fields.page_url')"
              :rules="[rules.urlValidation]"
              :blur="checkLinkSourceUrl"
              required>
              <template v-slot:required-symbol>
                <slot>
                  <span>*</span>
                </slot>
              </template>
            </va-text-input>
            <div class="source-url__disclaimer" v-if="!validUrl">
              <v-icon size="medium" color="info">mdi-information</v-icon>
              {{ $t("resources.ab_tests.first_party_cookie") }}
            </div>
            <div class="source-full-url">
              <a :href="sourceFullUrl" target="_blank">
                {{ sourceFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="1">
            <v-text-field
              class="number-input"
              v-model="originalWeight"
              :rules="[rules.weight]"
              suffix="%"
              type="text"
              hide-details="auto"
              :disabled="true">
              <template #label>
                <div class="input-label">
                  {{ $t("resources.ab_tests.fields.weight") }}<span class="required__icon">*</span>
                </div>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </div>
      <hr class="details-hr" />

      <div v-for="(_, index) in form.variants" :key="index" class="variant-wrapper">
        <div class="domain-input-wrapper__label">
          {{ form.variants[index].name }}
        </div>
        <v-row>
          <v-col :cols="3">
            <v-select
              v-model="form.variants[index].type"
              :items="SOURCES"
              :label="$t('resources.ab_tests.fields.source')"
              required
              :disabled="!isDraft && isEdit"
              item-value="value"
              item-text="text"
              append-icon="mdi-chevron-down"
              @change="handleVariantTypeChange"
              :menu-props="{
                offsetOverflow: true,
                offsetY: true,
              }">
              <template #label>
                <div class="input-label">
                  {{ $t("resources.ab_tests.fields.source") }}<span class="required__icon">*</span>
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col :cols="8" v-if="form.variants[index].type === 'page'">
            <div class="domain-slug-wrapper">
              <v-autocomplete
                class="page-select"
                v-model="domains[index]"
                :placeholder="$t('resources.ab_tests.placeholders.domain')"
                :rules="[v => !!v || $t('va.forms.required_field', { field: 'Domain' })]"
                :items="domainsList"
                append-icon="mdi-chevron-down"
                hide-details="auto"
                persistent-placeholder
                :disabled="!isDraft && isEdit"
                @change="onDomainChange(index)">
                <template #label>
                  <div class="input-label">
                    {{ $t("resources.ab_tests.fields.domain") }}<span class="required__icon">*</span>
                  </div>
                </template>
              </v-autocomplete>
              <va-autocomplete-input
                class="page-autocomplete"
                v-model="form.variants[index].page"
                :disabled="(!isDraft && isEdit) || !domains[index]"
                :label="$t('resources.ab_tests.fields.variant_slug')"
                :placeholder="$t('resources.ab_tests.placeholders.url')"
                reference="pages"
                :source="`variants.${index}.page`"
                item-value="@id"
                item-text="slug"
                required
                :filter="{ domain: domains[index], status: 2 }"
                search-query="slug"
                :min-chars="0"
                :items-per-page="5"
                showLoadMore>
                <template v-slot:required-symbol>
                  <slot>
                    <span>*</span>
                  </slot>
                </template>
              </va-autocomplete-input>
            </div>

            <div class="source-full-url">
              <a :href="variantFullUrl" target="_blank">
                {{ variantFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="8" v-else-if="form.variants[index].type === 'form'">
            <div class="domain-slug-wrapper">
              <va-autocomplete-input
                v-model.trim="variantFormDomain"
                class="form-autocomplete"
                :disabled="!isDraft && isEdit"
                :placeholder="$t('resources.ab_tests.placeholders.form')"
                :label="$t('resources.ab_tests.fields.form')"
                reference="external_forms"
                item-text="url"
                item-value="url"
                hide-details="auto"
                required
                :min-chars="0"
                :items-per-page="5"
                @input="concatVariantFormUrl(index)"
                :key="`${index}-variant-form-domain`"
                :filter="variantFormDomainFilter"
                showLoadMore>
                <template v-slot:required-symbol>
                  <slot>
                    <span>*</span>
                  </slot>
                </template>
              </va-autocomplete-input>
              <va-text-input
                v-model.trim="variantFormDomainParams"
                :disabled="!isDraft && isEdit"
                class="spacing-bottom--4"
                :label="$t('resources.ab_tests.fields.query_parameters')"
                :prefix="'/?'"
                :rules="[rules.queryParamsValidation]"
                @input="concatVariantFormUrl(index)">
              </va-text-input>
            </div>

            <div class="source-full-url">
              <a :href="variantFullUrl" target="_blank">
                {{ variantFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="8" v-else>
            <va-text-input
              class="spacing-bottom--4"
              v-model="form.variants[index].link"
              :label="$t('resources.ab_tests.fields.page_url')"
              :rules="[rules.urlValidation]"
              :error-messages="getLinkError(index)"
              :disabled="!isDraft && isEdit"
              required>
              <template v-slot:required-symbol>
                <slot>
                  <span>*</span>
                </slot>
              </template>
            </va-text-input>
            <div class="source-full-url">
              <a :href="variantFullUrl" target="_blank">
                {{ variantFullUrl }}
              </a>
            </div>
          </v-col>
          <v-col :cols="1">
            <v-text-field
              class="number-input"
              v-model="form.variants[index].weight"
              @change="numberFormat(form.variants[index].weight, index)"
              :error-messages="weightError"
              suffix="%"
              type="text"
              :min="0"
              :max="100"
              :disabled="splitEvenly || (!isDraft && isEdit)"
              hide-details="auto">
              <template #label>
                <div class="input-label">
                  {{ $t("resources.ab_tests.fields.weight") }}
                </div>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </div>
    </v-card>
    <!-- Statistics -->
    <v-card v-if="isEdit" class="statistics__card spacing-bottom--24">
      <div class="d-flex justify-space-between spacing-bottom--16 px-4">
        <v-card-title class="mb-0">{{ $t("resources.ab_tests.statistics") }} </v-card-title>

        <div class="d-flex align-center">
          <VaDatePicker
            :disabled="statisticsOption === $t('resources.ab_tests.buttons.summary')"
            hide-details
            :hide-title="false"
            :range="true"
            v-model="statsHistoricalSelectedDate"></VaDatePicker>

          <v-btn-toggle class="va-btn-toggle" v-model="statisticsOption" mandatory tile color="primary" group>
            <v-btn :value="AB_TEST_STATISTICS_OPTIONS.DAILY">
              {{ $t("resources.ab_tests.buttons.daily") }}
            </v-btn>

            <v-btn :value="AB_TEST_STATISTICS_OPTIONS.SUMMARY">
              {{ $t("resources.ab_tests.buttons.summary") }}
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <VaClientSideList
        v-if="statisticsOption === AB_TEST_STATISTICS_OPTIONS.DAILY"
        class="va-table"
        :items="statsHistoricalTableItems"
        :server-items-length="statsHistoricalTotalItems"
        :options.sync="statsHistoricalTableOptions"
        :loading="statisticsLoading">
        <template v-slot:item="{ item }">
          <VaClientSideList
            class="va-table va-table--spacing"
            :headers="statisticsTableHeaders"
            :items="item"
            hide-footer>
            <template v-slot:top>
              <div v-if="item[0].date" class="table-date">
                {{ item[0].date }}
              </div>
            </template>
            <template v-slot:item="{ item }">
              <tr v-if="item.name === $t('resources.ab_tests.difference')" class="custom-row">
                <td>
                  <div class="custom-row__headline">
                    {{ item.name }}
                  </div>
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.sessionsDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.callsDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.uniqueCallersDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.noConnectDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.revenueDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.rpcDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.averageCallLengthDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.callsConversionRateDiff" />
                </td>
                <td class="text-end">
                  <MetricCard :metricData="item.sessionsConversionRateDiff" />
                </td>
              </tr>
              <tr v-else>
                <td>
                  <div>
                    <span>{{ item.name }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.sessions }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.calls }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.uniqueCallers }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.noConnect }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>${{ item.revenue }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>${{ item.rpc }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ formatSecondsToHHMMSS(item.acl) }}</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.callsCr }}%</span>
                  </div>
                </td>
                <td>
                  <div class="text-end">
                    <span>{{ item.sessionsCr }}%</span>
                  </div>
                </td>
              </tr>
            </template>
          </VaClientSideList>
        </template>
      </VaClientSideList>

      <VaClientSideList
        v-else-if="statisticsOption === AB_TEST_STATISTICS_OPTIONS.SUMMARY"
        class="va-table"
        :headers="statisticsTableHeaders"
        :items="statisticsTableItems"
        :loading="statisticsLoading"
        hide-footer>
        <template v-slot:item="{ item }">
          <tr v-if="item.name === $t('resources.ab_tests.difference')" class="custom-row">
            <td>
              <div class="custom-row__headline">
                {{ item.name }}
              </div>
            </td>
            <td class="text-end">
              <MetricCard :metricData="item.sessionsDiff" />
            </td>
            <td class="text-end">
              <MetricCard :metricData="item.callsDiff" />
            </td>
            <td class="text-end">
              <MetricCard :metricData="item.uniqueCallersDiff" />
            </td>
            <td class="text-end">
              <MetricCard :metricData="item.noConnectDiff" />
            </td>
            <td class="text-end">
              <MetricCard :metricData="item.revenueDiff" />
            </td>
            <td>
              <MetricCard :metricData="item.rpcDiff" />
            </td>
            <td>
              <MetricCard :metricData="item.averageCallLengthDiff" />
            </td>
            <td>
              <MetricCard :metricData="item.callsConversionRateDiff" />
            </td>
            <td>
              <MetricCard :metricData="item.sessionsConversionRateDiff" />
            </td>
          </tr>
          <tr v-else>
            <td>
              <div>
                <span>{{ item.name }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.sessions }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.calls }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.uniqueCallers }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.noConnect }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>${{ item.revenue }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>${{ item.rpc }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ formatSecondsToHHMMSS(item.acl) }}</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.callsCr }}%</span>
              </div>
            </td>
            <td>
              <div class="text-end">
                <span>{{ item.sessionsCr }}%</span>
              </div>
            </td>
          </tr>
        </template>
      </VaClientSideList>
    </v-card>

    <v-card v-if="isEdit" class="statistics__card spacing-bottom--24">
      <div class="d-flex spacing-bottom--16 px-4">
        <v-card-title class="mb-0">{{ $t("resources.ab_tests.chart") }} </v-card-title>
      </div>

      <div class="px-4">
        <VaApexChart
          :series-options="chartProps.seriesOptions"
          :xaxis-values="chartProps.xaxisValues"
          :colors="chartProps.colors"
          :loading="chartStatisticsLoading"></VaApexChart>
      </div>
    </v-card>

    <!-- Last updated-->
    <v-card class="version-card" v-if="getChangelogArray.length > 0">
      <div class="version-table-header">
        {{ $t("resources.ab_tests.last_updated") }}
      </div>
      <table class="version-table">
        <tr v-for="version in getChangelogArray" :key="version['@id']">
          <td>
            {{ changeDateFormat(version.createdAt, isUserUS($store.state.user.timezone)) }}
            {{ $t("resources.ab_tests.at") }}
            {{ getHoursAndMinutes(version.createdAt) }}
            {{ $t("resources.ab_tests.by") }}
            {{ version.createdBy.firstName }} {{ version.createdBy.lastName }}
            {{ $t("resources.ab_tests.changed_the_test") }}
            {{ version.property === "active" ? $t("resources.ab_tests.status") : version.property }}
            {{ $t("resources.ab_tests.to") }}
            "{{
              version.newValue === "false"
                ? $t("resources.ab_tests.inactive")
                : version.newValue === "true"
                ? $t("resources.ab_tests.active")
                : version.newValue
            }}"
          </td>
        </tr>
        <tr v-if="form.changelog.length > 4">
          <td colspan="2">
            <v-btn
              v-if="!showFullChangelog"
              text
              color="primary"
              class="show-action-button"
              @click="showFullChangelog = true"
              :ripple="false">
              {{ $t("resources.ab_tests.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="show-action-button"
              @click="showFullChangelog = false"
              :ripple="false">
              {{ $t("resources.ab_tests.collapse") }}
            </v-btn>
          </td>
        </tr>
      </table>
    </v-card>
  </va-form>
</template>

<script>
  import crudFormMixin from "@/mixins/crudFormMixin";
  import connectedDomainsList from "@/mixins/connectedDomainsList";
  import { urlValidation } from "@/utility/urlValidation";
  import { isValidQueryParams } from "@/utility/isValidQueryParams";
  import {
    AB_POOL_TYPES,
    AB_REDIRECT_METHODS,
    AB_REDIRECT_TYPES,
    AB_SPLIT_TYPES,
    AB_TEST_STATUSES,
    AB_TEST_STATISTICS_OPTIONS,
  } from "@/constant";
  import VaClientSideList from "@/components/lists/VaClientSideList";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  import { changeDateFormat, getHoursAndMinutes } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import { dataProvider } from "@/@core/http";
  import MetricCard from "@/components/custom/MetricCard";
  import CopyText from "@/components/custom/CopyText";
  import Modal from "@/components/custom/Modal";
  import { formatDuration, intervalToDuration } from "date-fns"; // Add this import
  import PlusIcon from "@/components/icons/PlusIcon";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import VaDatePicker from "@/components/custom/VaDatePicker.vue";
  import VaApexChart from "@/components/custom/VaApexChart.vue";

  export default {
    name: "AbTestForm",
    components: {
      VaClientSideList,
      InfoTooltip,
      MetricCard,
      CopyText,
      Modal,
      PlusIcon,
      VaColorPicker,
      VaDatePicker,
      VaApexChart,
    },
    mixins: [crudFormMixin, connectedDomainsList],
    props: {
      isEdit: {
        type: Boolean,
        default: false,
      },
      abTest: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        urlValidation,
        isValidQueryParams,
        requiredFormFields: ["name"],
        originalWeight: "50",
        weightError: "",
        rules: {
          weight: v => (v > 100 || v < 0 ? this.$t("resources.ab_tests.invalid_weight") : true),
          urlValidation: v => !!urlValidation(v) || this.$t("generic.validation.url"),
          queryParamsValidation: v => !!isValidQueryParams(v) || this.$t("generic.validation.query_params"),
        },
        SOURCES: [
          {
            value: "page",
            text: "Domain",
          },
          {
            value: "link",
            text: "URL",
          },
          {
            value: "form",
            text: "FORM",
          },
        ],
        ORIGINAL_SOURCES: [
          {
            value: "page",
            text: "SiteRunner page",
          },
          {
            value: "link",
            text: "URL",
          },
          {
            value: "form",
            text: "FORM",
          },
        ],
        form: {
          variants: [
            {
              name: this.$t("resources.ab_tests.variant_1"),
              weight: "50",
              type: "form",
            },
          ],
          firstPartySubdomain: true,
          changelog: [],
          serverSide: true,
          splitType: AB_SPLIT_TYPES.CUSTOM,
          redirectType: AB_REDIRECT_TYPES.REPLACE,
          additionalGoals: [],
        },
        goals: [],
        selectedAdditionalGoals: [],
        isGoalCreateActive: false,
        additionalGoalSearch: "",
        hoveredAdditionalGoal: null,
        goalListLoading: false,
        displayDeleteGoalModal: false,
        newGoal: {},
        domains: [],
        formDomain: null,
        formDomainParams: null,
        variantFormDomain: null,
        variantFormDomainParams: null,
        validUrl: true,
        sourceFullUrl: "",
        variantFullUrl: "",
        // Statistics
        statisticsLoading: true,
        statisticsTableHeaders: [
          {
            text: this.$t("resources.ab_tests.variant_name"),
            value: "name",
            sortable: false,
          },
          {
            text: this.$t("resources.ab_tests.variant_sessions"),
            value: "sessions",
            sortable: false,
            align: "right",
            chart: {
              color: "#836af9",
              name: this.$t("resources.ab_tests.variant_sessions"),
              diffName: "sessionsDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.calls"),
            value: "calls",
            sortable: false,
            align: "right",
            chart: {
              color: "#ffe802",
              name: this.$t("resources.ab_tests.calls"),
              diffName: "callsDiff",
            },
          },
          {
            value: "statistics.uniqueCallersDiff",
            text: this.$t("resources.ab_tests.unique_callers"),
            sortable: false,
            align: "right",
            chart: {
              color: "#ff8131",
              name: this.$t("resources.ab_tests.unique_callers"),
              diffName: "uniqueCallersDiff",
            },
          },
          {
            value: "statistics.noConnectDiff",
            text: this.$t("resources.ab_tests.no_connect"),
            sortable: false,
            align: "right",
            chart: {
              color: "#299aff",
              name: this.$t("resources.ab_tests.no_connect"),
              diffName: "noConnectDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.revenue"),
            value: "revenue",
            sortable: false,
            align: "right",
            chart: {
              color: "#4f5d70",
              name: this.$t("resources.ab_tests.revenue"),
              diffName: "revenueDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.rpc"),
            value: "rpc",
            sortable: false,
            align: "right",
            chart: {
              color: "#28dac6",
              name: this.$t("resources.ab_tests.rpc"),
              diffName: "rpcDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.acl"),
            value: "acl",
            sortable: false,
            align: "right",
            chart: {
              color: "#f69fa9",
              name: this.$t("resources.ab_tests.acl"),
              diffName: "averageCallLengthDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.calls_cr"),
            value: "callsCr",
            sortable: false,
            align: "right",
            chart: {
              color: "#55ec2c",
              name: this.$t("resources.ab_tests.calls_cr"),
              diffName: "callsConversionRateDiff",
            },
          },
          {
            text: this.$t("resources.ab_tests.sessions_cr"),
            value: "sessionsCr",
            sortable: false,
            align: "right",
            chart: {
              color: "#d9750e",
              name: this.$t("resources.ab_tests.sessions_cr"),
              diffName: "sessionsConversionRateDiff",
            },
          },
        ],
        statisticsTableItems: [],
        AB_REDIRECT_METHODS,
        AB_REDIRECT_TYPES,
        AB_POOL_TYPES,
        AB_SPLIT_TYPES,
        splitEvenly: false,
        statePooling: false,
        showFullChangelog: false,
        changeDateFormat,
        isUserUS,
        getHoursAndMinutes,
        AB_TEST_STATUSES,
        AB_TEST_STATISTICS_OPTIONS,
        formDomainFilter: {},
        variantFormDomainFilter: {},
        showModalAfterSave: false,
        disableRedirect: false,
        statsHistoricalSelectedDate: [],
        statsHistoricalTotalItems: null,
        statsHistoricalTableItems: [],
        statsHistoricalTableOptions: {
          page: 1,
          itemsPerPage: 3,
        },
        statisticsOption: AB_TEST_STATISTICS_OPTIONS.SUMMARY,
        chartProps: {
          seriesOptions: [],
          xaxisValues: [],
        },
        chartStatisticsLoading: true,
      };
    },
    watch: {
      statsHistoricalTableOptions: {
        async handler(val) {
          await this.callHistoricalStats(val, false);
        },
        deep: true,
      },
      statsHistoricalSelectedDate: {
        async handler(val, oldVal) {
          if (val.length > 0) {
            this.$set(this.statsHistoricalTableOptions, "dateAfter", this.formatToIsoDate(val[0]));
            this.$set(this.statsHistoricalTableOptions, "dateBefore", this.formatToIsoDate(val[1]));
            this.$set(this.statsHistoricalTableOptions, "page", 1);

            await this.setChartValuesByDatePicker(val, this.statsHistoricalTableOptions);
          }

          if (oldVal.length && !val.length) {
            this.$set(this.statsHistoricalTableOptions, "dateAfter", null);
            this.$set(this.statsHistoricalTableOptions, "dateBefore", null);
            this.$set(this.statsHistoricalTableOptions, "page", 1);

            await this.setChartValuesByDatePicker(false, this.statsHistoricalTableOptions);
          }
        },
        deep: true,
      },
    },
    computed: {
      getChangelogArray() {
        if (!this.form.changelog) {
          return [];
        }
        if (this.showFullChangelog) {
          return this.form.changelog;
        } else {
          return this.form.changelog.slice(0, 4);
        }
      },
      domainFilter() {
        if (this.form.domain) {
          return { domain: this.form.domain, status: 2 };
        }

        return { status: 2 };
      },
      isDraft() {
        return this.form.status === AB_TEST_STATUSES.DRAFT;
      },
      isActive() {
        return this.form.status === AB_TEST_STATUSES.ACTIVE;
      },
      showScriptField() {
        return this.isEdit && this.abTest.type !== "page";
      },
    },
    created() {
      if (this?.abTest?.link && this.abTest.type === "form") {
        this.formDomainFilter = {
          q: this.abTest.link?.split("/?")[0]?.split("://")[1],
        };
      }

      if (this?.abTest?.variants[0]?.link && this.abTest.variants[0].type === "form") {
        this.variantFormDomainFilter = {
          q: this.abTest.variants[0].link?.split("/?")[0]?.split("://")[1],
        };
      }
    },
    async beforeMount() {
      if (!this.abTest) {
        this.form.status = AB_TEST_STATUSES.DRAFT;
        this.form.splitType = AB_SPLIT_TYPES.EVEN;
        this.splitEvenly = true;
        this.form.poolType = AB_POOL_TYPES.STATE;
        this.statePooling = true;
        this.form.type = "page";
        this.form.variants[0].type = "page";
        this.form.goal = null;
        this.selectedAdditionalGoals = [];
        this.form.additionalGoals = [];
        this.loadGoals();

        return;
      }

      this.form.serverSide = this.abTest.serverSide;
      this.domains = this.abTest.variants.map(variant => variant.page?.domain?.["@id"]);
      this.form.domain = this.abTest?.page?.domain["@id"];
      this.form.name = this.abTest.name;
      this.form.owner = this.abTest.owner?.["@id"];
      this.form.notes = this.abTest.notes;
      this.form.status = this.abTest.status;
      this.form.redirectType = this.abTest.redirectType;
      this.form.splitType = this.abTest.splitType;
      this.form.poolType = this.abTest.poolType;
      this.form.firstPartySubdomain = this.abTest.firstPartySubdomain;
      this.form.script = this.abTest.script;
      this.form.goal = this.abTest?.goal ? this.abTest?.goal["@id"] : null;
      this.selectedAdditionalGoals = this.abTest?.additionalGoals ? this.abTest.additionalGoals : [];
      this.form.additionalGoals = this.abTest?.additionalGoals
        ? this.abTest.additionalGoals.map(goal => goal["@id"])
        : [];

      this.form.status = this.abTest.status;
      this.form.type = this.abTest.type;
      this.form.link = this.abTest?.link;

      const { data } = await dataProvider.customHttpCall("GET", `ab_tests/${this.abTest.id}/changelog`);
      this.form.changelog = data;

      await this.callHistoricalStats(this.statsHistoricalTableOptions);

      this.form.variants = this.abTest.variants.map(variant => {
        if (variant.type === "page") {
          this.variantFullUrl =
            this.abTest.status !== AB_TEST_STATUSES.DRAFT
              ? `${"https://" + variant.page.domain.url}/${variant.page.slug}`
              : "";
        } else {
          this.variantFullUrl = this.abTest.status !== AB_TEST_STATUSES.DRAFT ? variant?.link : "";
        }

        return {
          name: variant.name,
          weight: variant.weight.toString(),
          type: variant.type,
          page: variant.page?.["@id"],
          link: variant?.link,
          "@id": variant["@id"],
        };
      });

      if (this.abTest.splitType === AB_SPLIT_TYPES.EVEN) {
        this.statePooling = this.abTest.poolType === AB_POOL_TYPES.STATE;

        this.splitEvenly = true;
        this.originalWeight = this.form.variants[0]?.weight || "100";
      } else {
        this.originalWeight = 100 - this.form.variants.reduce((acc, variant) => acc + parseInt(variant.weight), 0);
      }

      this.form.link = this.abTest?.link;

      if (this.abTest.type === "form") {
        this.formDomain = this.abTest.link.split("/?")[0].split("://")[1];
        this.formDomainParams = this.abTest.link.split("/?")[1];
      }

      if (this.abTest.type === "page") {
        this.sourceFullUrl =
          this.abTest.status !== AB_TEST_STATUSES.DRAFT
            ? `${"https://" + this.abTest.page.domain.url}/${this.abTest.page.slug}`
            : "";

        this.disableRedirect = false;
      } else {
        this.sourceFullUrl = this.abTest.status !== AB_TEST_STATUSES.DRAFT ? this.abTest.link : "";
        this.disableRedirect = true;
      }

      if (this.abTest.variants[0]?.type === "form") {
        this.variantFormDomain = this.abTest.variants[0].link.split("/?")[0].split("://")[1];
        this.variantFormDomainParams = this.abTest.variants[0].link.split("/?")[1];
        this.form.variants[0].link = this.abTest.variants[0].link;
      }
      this.setStatisticsTableData(this.abTest);
      this.formOriginalState = JSON.stringify(this.form);
      this.loadGoals();
    },
    methods: {
      async callHistoricalStats(options, fillChartData = true) {
        this.statisticsLoading = true;
        if (fillChartData) {
          this.chartStatisticsLoading = true;
        }
        try {
          const { data, total } = await dataProvider.customHttpCall(
            "GET",
            `ab_tests/${this.abTest.id}/stats/historical?page=${options.page}&itemsPerPage=${options.itemsPerPage}&date[before]=${options.dateBefore}&date[after]=${options.dateAfter}`,
          );

          this.setHistoricalStatsTableItems(data);
          this.statsHistoricalTotalItems = total;

          if (fillChartData) {
            this.setChartValues(data);
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.statisticsLoading = false;
          if (fillChartData) {
            this.chartStatisticsLoading = false;
          }
        }
      },
      setChartValues(data) {
        const headersWithChart = this.statisticsTableHeaders.filter(header => header.chart);

        this.chartProps.seriesOptions = headersWithChart
          .map(header => {
            const { name, diffName } = header.chart;
            const seriesData = data.map(item => item[diffName]).reverse();

            // Return object only if seriesData has at least one truthy item
            if (!seriesData.some(v => v != null)) return null;

            return {
              name,
              data: seriesData,
            };
          })
          .filter(Boolean); // Remove nulls

        this.chartProps.colors = headersWithChart.map(header => header.chart.color);

        this.chartProps.xaxisValues = data
          .map(item => {
            const date = new Date(item.date);

            return `${date.getDate()}/${date.getMonth() + 1}`;
          })
          .reverse();
      },
      async setChartValuesByDatePicker(dateValues, options) {
        this.chartStatisticsLoading = true;

        let itemsPerPage = 3;
        if (dateValues) {
          itemsPerPage = this.calculateDaysBetweenDates(dateValues);
        }

        try {
          const { data } = await dataProvider.customHttpCall(
            "GET",
            `ab_tests/${this.abTest.id}/stats/historical?page=${options.page}&itemsPerPage=${itemsPerPage}&date[before]=${options.dateBefore}&date[after]=${options.dateAfter}`,
          );

          this.setChartValues(data);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.chartStatisticsLoading = false;
        }
      },
      calculateDaysBetweenDates(dates) {
        const start = new Date(dates[0]);
        const end = new Date(dates[1]);

        const diffMs = end - start;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const totalDays = diffDays + 1;

        return totalDays;
      },
      handleCloseModalButton() {
        this.$route.meta.disableRedirect = false;
        this.$router.push({ name: "ab_tests_list" });
      },
      callModalAfterSave() {
        this.showModalAfterSave = this.form.type !== "page";
      },
      serverSideHandler() {
        this.form.redirectType = AB_REDIRECT_TYPES.REPLACE;
      },
      async saveGoal() {
        if (!this.$refs.goalForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("goals", {
            data: {
              name: this.newGoal.name,
              color: this.newGoal.color ?? "#FFFFFF",
            },
          });
          this.newGoal = {};
          this.$admin.toast.success(this.$t("resources.ab_tests.goal_create_success"));
          this.goals.push(response.data);
          this.goals.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isGoalCreateActive = false;
        }
      },
      getGoalChipColor(item) {
        let hex = item.color;
        let r = parseInt(hex?.slice(1, 3), 16);
        let g = parseInt(hex?.slice(3, 5), 16);
        let b = parseInt(hex?.slice(5, 7), 16);

        return "rgba(" + r + ", " + g + ", " + b + ", " + 0.12 + ")";
      },
      async loadGoals() {
        this.goalListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("goals", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.goals = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.goalListLoading = false;
        }
      },
      deleteGoal(item) {
        this.displayDeleteGoalModal = true;
        this.goalToDelete = item;
      },
      async confirmDeleteGoal() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `goals/${this.goalToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.ab_tests.goal_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.ab_tests.goal_delete_error"));
        } finally {
          this.goals = this.goals.filter(item => item["@id"] !== this.goalToDelete["@id"]);
          if (this.form.goal && this.form.goal === this.goalToDelete["@id"]) {
            this.form.goal = null;
          }

          this.selectedAdditionalGoals = this.selectedAdditionalGoals.filter(
            item => item["@id"] !== this.goalToDelete["@id"],
          );
        }
        this.displayDeleteGoalModal = false;
        this.goalToDelete = {};
      },
      cancelDeleteGoal() {
        this.goalToDelete = {};
        this.displayDeleteGoalModal = false;
      },
      handleSourceTypeChange(sourceType) {
        this.validUrl = true;
        this.form.link = "";
        this.formDomain = null;
        this.formDomainParams = null;
        this.form.firstPartySubdomain = true;
        this.form.serverSide = true;

        if (sourceType === "page") {
          this.form.link = null;
          this.disableRedirect = false;
        } else {
          this.disableRedirect = true;
          this.form.page = null;
          if (sourceType !== this.form.variants[0].type) this.form.variants[0].link = null;
        }

        if (sourceType !== this.form.variants[0].type) this.form.variants[0].type = sourceType;
      },
      handleVariantTypeChange(variantType) {
        this.variantFormDomain = null;
        this.variantFormDomainParams = "";

        if (variantType === "page") {
          this.form.variants[0].link = null;
        } else {
          this.form.variants[0].page = null;
        }

        this.form.variants[0].link = "";
      },
      async checkLinkSourceUrl(item) {
        if (!urlValidation(item.$refs.input.value)) return;

        const { data } = await dataProvider.customHttpCall("POST", `ab_tests/check_url`, {
          url: `${item.$refs.input.value}`,
        });

        this.form.firstPartySubdomain = data.valid;

        this.validUrl = data.valid;
      },
      async checkFormSourceUrl(item) {
        if (!item.target.value) return;

        const { data } = await dataProvider.customHttpCall("POST", `ab_tests/check_url`, {
          url: `https://${item.target.value}`,
        });

        this.form.firstPartySubdomain = data.valid;

        this.validUrl = data.valid;
      },
      concatFormUrl() {
        this.formDomainFilter = {};

        this.form.link = `
        ${this.formDomain ? "https://" + this.formDomain : ""}${
          this.formDomainParams ? "/?" + this.formDomainParams : ""
        }`.trim();
      },
      concatVariantFormUrl(index) {
        this.variantFormDomainFilter = {};

        this.form.variants[index].link = `
        ${this.variantFormDomain ? "https://" + this.variantFormDomain : ""}${
          this.variantFormDomainParams ? "/?" + this.variantFormDomainParams : ""
        }`.trim();
      },
      formatSecondsToHHMMSS(seconds) {
        if (!seconds) return "00:00:00";

        const duration = intervalToDuration({ start: 0, end: parseInt(seconds) * 1000 });

        return formatDuration(duration, { format: ["hours", "minutes", "seconds"], zero: true })
          .replace(/(\d+) hours?/, (_, h) => h.padStart(2, "0"))
          .replace(/(\d+) minutes?/, (_, m) => m.padStart(2, "0"))
          .replace(/(\d+) seconds?/, (_, s) => s.padStart(2, "0"))
          .replace(/\s/g, ":");
      },
      onDomainChange(index) {
        if (!this.form.variants[index].domain) {
          this.form.variants[index].page = null;
        }
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
      setHistoricalStatsTableItems(dataStatsHistorical) {
        this.statsHistoricalTableItems = dataStatsHistorical.map(stat => {
          return [
            {
              date: this.formatFromIsoDate(stat.date),
              name: this.$t("resources.ab_tests.original_test"),
              sessions: stat.sessions,
              calls: stat.calls,
              revenue: stat.revenue,
              rpc: stat.rpc,
              acl: stat.averageCallLength,
              callsCr: stat.callsConversionRate,
              sessionsCr: stat.sessionsConversionRate,
              noConnect: stat.noConnect,
              uniqueCallers: stat.uniqueCallers,
            },
            {
              name: this.$t("resources.ab_tests.variant_1"),
              sessions: stat.variantSessions,
              calls: stat.variantCalls,
              revenue: stat.variantRevenue,
              rpc: stat.variantRpc,
              acl: stat.variantAverageCallLength,
              callsCr: stat.variantCallsConversionRate,
              sessionsCr: stat.variantSessionsConversionRate,
              noConnect: stat.variantNoConnect,
              uniqueCallers: stat.variantUniqueCallers,
            },
            {
              name: this.$t("resources.ab_tests.difference"),
              sessionsDiff: stat.sessionsDiff,
              callsDiff: stat.callsDiff,
              revenueDiff: stat.revenueDiff,
              rpcDiff: stat.rpcDiff,
              averageCallLengthDiff: stat.averageCallLengthDiff,
              callsConversionRateDiff: stat.callsConversionRateDiff,
              sessionsConversionRateDiff: stat.sessionsConversionRateDiff,
              noConnectDiff: stat.noConnectDiff,
              uniqueCallersDiff: stat.uniqueCallersDiff,
            },
          ];
        });
      },
      setStatisticsTableData(abTest) {
        if (!abTest.statistics) {
          this.statisticsLoading = false;

          return;
        }
        this.statisticsTableItems = [
          {
            name: this.$t("resources.ab_tests.original_test"),
            sessions: abTest.statistics.sessions,
            calls: abTest.statistics.calls,
            revenue: abTest.statistics.revenue,
            rpc: abTest.statistics.rpc,
            acl: abTest.statistics.averageCallLength,
            callsCr: abTest.statistics.callsConversionRate,
            sessionsCr: abTest.statistics.sessionsConversionRate,
            noConnect: abTest.statistics.noConnect,
            uniqueCallers: abTest.statistics.uniqueCallers,
          },
          ...abTest.variants.map(variant => {
            return {
              name: variant.name,
              sessions: abTest.statistics.variantSessions,
              calls: abTest.statistics.variantCalls,
              revenue: abTest.statistics.variantRevenue,
              rpc: abTest.statistics.variantRpc,
              acl: abTest.statistics.variantAverageCallLength,
              callsCr: abTest.statistics.variantCallsConversionRate,
              sessionsCr: abTest.statistics.variantSessionsConversionRate,
              noConnect: abTest.statistics.variantNoConnect,
              uniqueCallers: abTest.statistics.variantUniqueCallers,
            };
          }),
          {
            name: this.$t("resources.ab_tests.difference"),
            sessionsDiff: abTest.statistics.sessionsDiff,
            callsDiff: abTest.statistics.callsDiff,
            revenueDiff: abTest.statistics.revenueDiff,
            rpcDiff: abTest.statistics.rpcDiff,
            averageCallLengthDiff: abTest.statistics.averageCallLengthDiff,
            callsConversionRateDiff: abTest.statistics.callsConversionRateDiff,
            sessionsConversionRateDiff: abTest.statistics.sessionsConversionRateDiff,
            noConnectDiff: abTest.statistics.noConnectDiff,
            uniqueCallersDiff: abTest.statistics.uniqueCallersDiff,
          },
        ];
        this.statisticsLoading = false;
      },
      formatFromIsoDate(isoDate) {
        const date = new Date(isoDate);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
      },
      formatToIsoDate(date) {
        return new Date(date + "T00:00:00Z").toISOString();
      },
      numberFormat(value, index) {
        value = value.replace(/\D/g, "");
        if (parseInt(value) > 100) {
          this.form.variants[index].weight = "100";
        } else if (parseInt(value) < 0) {
          this.form.variants[index].weight = "0";
        } else if (value === "") {
          this.form.variants[index].weight = "0";
        } else {
          this.form.variants[index].weight = value;
        }
        this.originalWeight = 100 - this.form.variants.reduce((acc, variant) => acc + parseInt(variant.weight), 0);
        if (this.originalWeight < 0 || this.originalWeight > 100) {
          this.weightError = this.$t("resources.ab_tests.invalid_weight");
        } else {
          this.weightError = "";
        }
      },
      prepareBeforeSubmit(saveStatus = AB_TEST_STATUSES.DRAFT) {
        if (this.requiredFieldsSet) this.form.status = saveStatus;
        if (this.selectedAdditionalGoals) {
          this.form.additionalGoals = this.selectedAdditionalGoals.map(label => label["@id"]);
        }

        if (saveStatus !== AB_TEST_STATUSES.ACTIVE) {
          this.disableRedirect = false;
        }

        setTimeout(() => {
          this.$route.meta.disableRedirect = false;
          this.submit();
        }, 0);
      },
      async submit() {
        this.setSubmitAttempted();
        if (this.weightError) {
          this.finishedSaving();

          return;
        }

        this.$refs.form.onSubmit();
      },
      handleAfterSave(data) {
        this.form.script = data.script;
        this.showModalAfterSave = this.disableRedirect;
      },
      getLinkError(index) {
        return this.$refs.form?.formState?.errors
          ? this.$refs.form.formState.errors[`variants[${index}].link`]
          : undefined;
      },
      splitEvenlyHandler() {
        if (this.splitEvenly) {
          this.form.variants.forEach(variant => {
            const calculateWeight = `${100 / (this.form.variants.length + 1)}`;
            variant.weight = calculateWeight.substring(0, 5);
            this.originalWeight = calculateWeight.substring(0, 5);
          });
          if (this.form.variants.length === 0) {
            this.originalWeight = "100";
          }
          this.statePooling = true;
          this.form.splitType = AB_SPLIT_TYPES.EVEN;
          this.form.poolType = AB_POOL_TYPES.STATE;
        } else {
          this.form.splitType = AB_SPLIT_TYPES.CUSTOM;
          this.form.poolType = AB_POOL_TYPES.GENERAL;
        }
      },
      statePoolingHandler() {
        if (this.statePooling) {
          this.form.poolType = AB_POOL_TYPES.STATE;
        } else {
          this.form.poolType = AB_POOL_TYPES.GENERAL;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .number-input {
    &::v-deep {
      input {
        padding-right: 0;
      }
      .v-input__slot {
        padding: 0 !important;
        min-height: unset !important;
      }

      .v-text-field__suffix {
        margin: 0;
        padding-right: 5px;
        padding-left: 0;
      }

      .v-input__append-outer {
        width: 0;
        margin: 0;
        align-self: center;

        button {
          margin-left: 4px;
          color: get-color("icon");
        }
      }
      &.v-input--is-disabled {
        .v-messages {
          color: get-color("error") !important;
        }
      }
    }
  }

  .variant-domain-input {
    margin-top: 0;
    padding-top: 19px;
    width: 100%;
  }

  .variant-wrapper {
    &:first-child {
      background-color: indianred;
    }

    &:nth-child(1) {
      background-color: greenyellow;
    }
    padding-top: 16px;
  }

  .source-url {
    &__disclaimer {
      padding: 8px;
      border-radius: 4px;
      background-color: #0d6efd1e;
      @include text-caption();
      color: get-color("body");
      display: flex;

      .v-icon {
        margin-right: 8px;
      }
    }
  }

  .domain-slug-wrapper {
    display: flex;
    gap: 24px;

    .form-autocomplete {
      max-width: 400px;
    }
  }

  .btn-custom-add {
    color: get-color("secondary-btn") !important;
    border-color: get-color("primary-65");

    i {
      color: get-color("secondary-btn") !important;
      margin-right: 4px;
    }
  }

  .details-hr {
    border: 1px solid get-color("border");
    border-top-width: 0;
  }

  .domain-input-wrapper {
    margin: 16px 0;

    &__label {
      @include text-caption();
      color: get-color("subtitle");
      margin-bottom: 16px;
    }
  }
  .page-autocomplete {
    &::v-deep {
      .v-input--is-disabled {
        .v-messages {
          margin-top: 8px;
          color: get-color("error") !important;
        }
      }
    }
  }

  .page-select {
    min-width: 256px;
    margin-bottom: 8px;
  }

  .statistics__card {
    padding: 16px 0 0 0;
    .reset-statistics__btn {
      padding: 12px 24px 12px 16px !important;
      &::v-deep {
        i {
          margin-right: 6px;
        }
      }
    }

    .va-table {
      &::v-deep {
        table {
          border-top: none !important;
          border-bottom: none !important;

          .custom-row {
            background-color: get-color("hover");
            td {
              border-bottom: 1px solid get-color("border");
            }
            &__headline {
              text-transform: uppercase;
              color: get-color("subtitle");
              @include text-caption-bold();
            }
          }

          .table-date {
            color: get-color("headline");
            border-top: 1px solid get-color("border");
            border-bottom: 1px solid get-color("border");
            background: get-color("element-bg-200");
            @include text-body-2-bold();
            padding: 4px 8px;
          }
        }
      }
      &--spacing {
        padding-bottom: 16px;

        &:last-child {
          padding-bottom: 0;
        }
      }
    }
  }

  .server-side-block {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 12px;
    background-color: get-color("element-bg-200");
    border-radius: 6px;
    margin-bottom: 16px;

    &__text {
      @include text-body-2-medium();
      color: get-color("body");
    }
  }

  .split-block {
    display: flex;
    width: fit-content;
    padding: 8px;
    background-color: get-color("element-bg-200");
    border-radius: 6px;

    &__text {
      @include text-body-2-medium();
      color: get-color("body");
    }

    &__subtext {
      @include text-caption();
      color: get-color("body");
    }

    &__column {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    &__row {
      display: flex;
      align-items: center;
      &:nth-child(2) {
        margin-top: 8px;
      }
    }

    &__checkbox {
      &::v-deep {
        .v-input--selection-controls__input {
          width: 16px;
          height: 16px;
          margin-right: 4px;
        }

        .v-input--selection-controls__ripple {
          width: 16px;
          height: 16px;
          left: -7px;
          top: calc(50% - 15px);
        }
      }
    }

    &__switch {
      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }

  .version-card {
    padding: 16px 0 0 0;
  }

  .version-table-header {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 16px;
    padding: 0 16px;
    text-transform: uppercase;
  }

  .version-table {
    width: 100%;
    border-spacing: 0;

    tr {
      td {
        width: 50%;
        padding: 7px 8px;
        border-top: 1px solid get-color("border");
        color: get-color("body");
        @include text-body-2();
        height: 48px;

        ::v-deep {
          .v-chip {
            height: 24px;
            &.default,
            &.v-chip--no-color {
              background-color: get-color("element-bg-200") !important;
              .v-chip__content {
                color: get-color("subtitle") !important;
              }
            }
            &.success {
              background-color: get-color("success-120") !important;
              .v-chip__content {
                color: get-color("success") !important;
              }
            }
          }
        }

        .show-action-button {
          padding: 0;
          height: unset !important;
          width: unset;
          min-width: unset;

          &::before {
            background-color: unset;
          }
        }
      }
    }
  }

  .details-title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .variants-title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: top;
    width: 100%;
  }

  .active-chip {
    color: get-color("info");
  }

  .status-chip {
    @include text-caption();
    height: unset;
    padding: 4px 12px;
  }

  .inactive-chip {
    background-color: get-color("element-bg-100") !important;
    border: 1px solid get-color("border") !important;
    .v-chip__content {
      color: get-color("disabled") !important;
    }
  }

  .source-full-url {
    @include text-caption();
    a {
      color: get-color("body");
    }
  }

  .script-description {
    margin-top: 8px;
    @include text-caption();
    color: get-color("body");
  }

  .script-modal {
    background-color: red !important;
    font-size: 48px !important;

    &-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 24px;
    }

    &-label {
      @include text-caption-bold();
      color: get-color("subtitle");
    }

    &-content {
      padding: 12px;
      border-radius: 4px;
      border: 1px solid get-color("border");
      background: get-color("hover");
      color: get-color("subtitle");
      @include text-body-2();
    }

    &-action-wrapper {
      border-top: 1px solid get-color("border");
      margin: -8px -24px 0 -24px;
      padding: 24px 24px 0 24px;
    }

    &-action-button {
      margin: 0;
      padding: 12px 24px;
      border-radius: 4px;
      background: get-color("primary");
      color: get-color("same-white");
      justify-content: center;
      @include button-medium();
    }
  }

  .goal-title {
    @include text-body-2();
  }

  .goal-badge {
    // Important needed to overwrite default avatar properties
    border: 1px solid get-color("border") !important;
  }

  .goal-wrapper {
    @include text-button-small();
    color: get-color("secondary-btn");
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    i {
      transition: none;
      color: get-color("secondary-btn") !important;
    }

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      i {
        color: get-color("same-hover") !important;
      }
    }
  }

  #goal-creator {
    border: 1px solid get-color("border");
    border-radius: 4px;
    padding: 16px;
  }

  .goal-select-item {
    padding: 8px 0;
    &:hover {
      .goal-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .page-elements-card-subtitle {
    @include text-body-2();
    color: get-color("subtitle");
    margin: 0;
  }

  .goal-selector {
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .page-alternative-content-click-parameters-title {
    @include text-body-2-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-elements-panel-wrapper {
    ::v-deep .v-expansion-panel-header {
      min-height: unset;
    }
  }

  .page-element-autocomplete {
    ::v-deep {
      .v-input.v-select.v-autocomplete--with-goal,
      .v-input.v-autocomplete.v-autocomplete--with-goal {
        padding-top: 25px;
      }
    }
  }
  .goals-empty-subtitle {
    @include text-subtitle-2();
    color: get-color("subtitle") !important;
    margin-bottom: 0;
  }

  .goal-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .goal-add-button {
    color: get-color("secondary-btn");
    @include text-button-medium();
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      ::v-deep path {
        fill: get-color("same-hover") !important;
      }
    }
  }

  .goal-cancel-button {
    border-color: get-color("icon");
  }

  .goal-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .goal-form {
    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
      }
    }
  }

  .goal-name-input {
    margin-bottom: 2px;
  }

  .goal-primary-select {
    margin-bottom: 2px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .additional-goal-autocomplete {
    ::v-deep {
      .v-select__selections input::placeholder {
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }

      .v-list-item {
        @include text-body-2();
      }
    }
  }

  .additional-goal-autocomplete:not(.v-autocomplete--search-added) {
    ::v-deep {
      .v-select__selections::after {
        position: absolute;
        content: attr(data-placeholder);
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }
    }
  }

  .additional-goal-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .goal-select-wrapper-block {
    display: flex;
    gap: 16px;
  }

  .goal-select-wrapper-block-item {
    flex: 1;
  }

  .additional-goal-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .additional-goal-chip {
    @include text-caption();
  }

  .owner-subtitle {
    @include text-caption();
    color: get-color("subtitle");
  }

  .va-btn-toggle {
    padding-top: 6px;
    border-radius: 4px;
    margin-left: 16px;

    .v-btn {
      margin: 0;
      box-shadow: none !important;
      border: 1px solid get-color("secondary-btn");
      color: get-color("secondary-btn");
    }
  }

  .chart-item {
    font-size: 12px;
    color: rgba(46, 38, 61, 0.4);

    &--values {
      padding-bottom: 18px;
    }
  }

  .chart {
    border: 1px solid #f2f2f3;
    margin: 4px;
  }
</style>
